function getCookie(name) {
    if (typeof document === 'undefined')
        return undefined;
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2)
        return parts.pop()?.split(';').shift();
    return undefined;
}
let memoryCsrfToken = undefined;
export class AatmanovaClient {
    baseUrl;
    constructor(baseUrl) {
        this.baseUrl = baseUrl;
    }
    getBaseUrl() {
        return this.baseUrl;
    }
    async request(path, options = {}) {
        const url = `${this.baseUrl}${path}`;
        const headers = {
            ...options.headers,
        };
        if (!(options.body instanceof FormData)) {
            headers['Content-Type'] = 'application/json';
        }
        // Attach CSRF token header for state-changing requests in browser environment
        const safeMethods = ['GET', 'HEAD', 'OPTIONS'];
        if (options.method && !safeMethods.includes(options.method.toUpperCase())) {
            const csrf = getCookie('_csrf') || memoryCsrfToken;
            if (csrf) {
                headers['X-CSRF-Token'] = csrf;
            }
        }
        const response = await fetch(url, {
            ...options,
            headers,
            credentials: 'include',
        });
        // Extract CSRF token from response headers if present to maintain token robustness
        const csrfHeader = response.headers.get('x-csrf-token');
        if (csrfHeader) {
            memoryCsrfToken = csrfHeader;
        }
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error?.message || errorData.message || `Request failed with status ${response.status}`);
        }
        if (response.status === 204) {
            return;
        }
        return response.json();
    }
    async requestBlob(path, options = {}) {
        const url = `${this.baseUrl}${path}`;
        const headers = {
            ...options.headers,
        };
        if (!(options.body instanceof FormData)) {
            headers['Content-Type'] = 'application/json';
        }
        // Attach CSRF token header for state-changing requests in browser environment
        const safeMethods = ['GET', 'HEAD', 'OPTIONS'];
        if (options.method && !safeMethods.includes(options.method.toUpperCase())) {
            const csrf = getCookie('_csrf') || memoryCsrfToken;
            if (csrf) {
                headers['X-CSRF-Token'] = csrf;
            }
        }
        const token = localStorage.getItem('aatmanova_token');
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }
        const response = await fetch(url, {
            ...options,
            headers,
            credentials: 'include',
        });
        // Extract CSRF token from response headers if present to maintain token robustness
        const csrfHeader = response.headers.get('x-csrf-token');
        if (csrfHeader) {
            memoryCsrfToken = csrfHeader;
        }
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error?.message || errorData.message || `Request failed with status ${response.status}`);
        }
        return response.blob();
    }
    // Health check
    async getHealth() {
        return this.request('/api/health');
    }
    // Audit Logs Domain
    async getAuditLogs(entity, entityId, limit = 50) {
        return this.request(`/api/v1/audit-logs?entity=${entity}&entityId=${entityId}&limit=${limit}`);
    }
    // Auth Domain
    async login(input) {
        return this.request('/api/v1/auth/login', {
            method: 'POST',
            body: JSON.stringify(input),
        });
    }
    async register(input) {
        return this.request('/api/v1/auth/register', {
            method: 'POST',
            body: JSON.stringify(input),
        });
    }
    async logout() {
        return this.request('/api/v1/auth/logout', {
            method: 'POST',
        });
    }
    async getCurrentUser() {
        return this.request('/api/v1/auth/me');
    }
    // Products Domain
    async getProducts(filters = {}) {
        const params = new URLSearchParams();
        Object.entries(filters).forEach(([key, val]) => {
            if (val !== undefined && val !== null) {
                params.append(key, String(val));
            }
        });
        const query = params.toString();
        return this.request(`/api/v1/products${query ? `?${query}` : ''}`);
    }
    async getProduct(idOrSlug) {
        return this.request(`/api/v1/products/${idOrSlug}`);
    }
    async createProduct(input) {
        return this.request('/api/v1/products', {
            method: 'POST',
            body: JSON.stringify(input),
        });
    }
    async updateProduct(id, input) {
        return this.request(`/api/v1/products/${id}`, {
            method: 'PATCH',
            body: JSON.stringify(input),
        });
    }
    async deleteProduct(id) {
        return this.request(`/api/v1/products/${id}`, {
            method: 'DELETE',
        });
    }
    async syncProducts() {
        return this.request('/api/v1/products/sync', {
            method: 'POST',
        });
    }
    async reindexProducts() {
        return this.request('/api/v1/products/reindex', {
            method: 'POST',
        });
    }
    async resetInventory(password) {
        return this.requestBlob('/api/v1/products/reset-inventory', {
            method: 'POST',
            body: JSON.stringify({ password }),
        });
    }
    async exportProducts(format = 'csv') {
        return this.requestBlob(`/api/v1/products/export?format=${format}`);
    }
    async importProducts(file, filename, status = 'DRAFT', dryRun = false, importId) {
        const formData = new FormData();
        formData.append('file', file, filename);
        const query = new URLSearchParams({ status, dryRun: String(dryRun) });
        if (importId) {
            query.append('importId', importId);
        }
        return this.request(`/api/v1/products/import?${query.toString()}`, {
            method: 'POST',
            body: formData,
        });
    }
    async importZip(file, status = 'DRAFT', dryRun = false, importId) {
        const formData = new FormData();
        formData.append('file', file, 'import.zip');
        const query = new URLSearchParams({ status, dryRun: String(dryRun) });
        if (importId) {
            query.append('importId', importId);
        }
        return this.request(`/api/v1/products/import/zip?${query.toString()}`, {
            method: 'POST',
            body: formData,
        });
    }
    async getImportStatus(importId) {
        return this.request(`/api/v1/products/import/status/${importId}`);
    }
    async bulkStatusUpdate(ids, status) {
        return this.request('/api/v1/products/bulk-status-update', {
            method: 'POST',
            body: JSON.stringify({ ids, status }),
        });
    }
    async bulkPriceUpdate(ids, percent) {
        return this.request('/api/v1/products/bulk-price-update', {
            method: 'POST',
            body: JSON.stringify({ ids, percent }),
        });
    }
    async bulkDelete(ids) {
        return this.request('/api/v1/products/bulk-delete', {
            method: 'POST',
            body: JSON.stringify({ ids }),
        });
    }
    // Categories
    async getCategories() {
        return this.request('/api/v1/products/categories');
    }
    async createCategory(input) {
        return this.request('/api/v1/products/categories', {
            method: 'POST',
            body: JSON.stringify(input),
        });
    }
    async updateCategory(id, input) {
        return this.request(`/api/v1/products/categories/${id}`, {
            method: 'PATCH',
            body: JSON.stringify(input),
        });
    }
    async deleteCategory(id) {
        return this.request(`/api/v1/products/categories/${id}`, {
            method: 'DELETE',
        });
    }
    async createSubcategory(input) {
        return this.request('/api/v1/products/subcategories', {
            method: 'POST',
            body: JSON.stringify(input),
        });
    }
    async updateSubcategory(id, input) {
        return this.request(`/api/v1/products/subcategories/${id}`, {
            method: 'PATCH',
            body: JSON.stringify(input),
        });
    }
    async deleteSubcategory(id) {
        return this.request(`/api/v1/products/subcategories/${id}`, {
            method: 'DELETE',
        });
    }
    // Admin Domain
    async getSystemHealth() {
        return this.request('/api/v1/admin/health');
    }
    async getAdminStats(days = 30) {
        return this.request(`/api/v1/admin/stats?days=${days}`);
    }
    // Orders Domain
    async createOrder(input) {
        return this.request('/api/v1/orders', {
            method: 'POST',
            body: JSON.stringify(input),
        });
    }
    async getOrders(filters = {}) {
        const params = new URLSearchParams();
        Object.entries(filters).forEach(([key, val]) => {
            if (val !== undefined && val !== null && val !== 'ALL') {
                params.append(key, String(val));
            }
        });
        const query = params.toString();
        return this.request(`/api/v1/orders${query ? `?${query}` : ''}`);
    }
    async getOrder(id) {
        return this.request(`/api/v1/orders/${id}`);
    }
    async updateOrder(id, input) {
        return this.request(`/api/v1/orders/${id}`, {
            method: 'PATCH',
            body: JSON.stringify(input),
        });
    }
    // Contacts Domain
    async createContactMessage(input) {
        return this.request('/api/v1/contacts', {
            method: 'POST',
            body: JSON.stringify(input),
        });
    }
    async getContactMessages(filters = {}) {
        const params = new URLSearchParams();
        Object.entries(filters).forEach(([key, val]) => {
            if (val !== undefined && val !== null && val !== 'ALL') {
                params.append(key, String(val));
            }
        });
        const query = params.toString();
        return this.request(`/api/v1/contacts${query ? `?${query}` : ''}`);
    }
    async getContactMessage(id) {
        return this.request(`/api/v1/contacts/${id}`);
    }
    async updateContactMessageStatus(id, status) {
        return this.request(`/api/v1/contacts/${id}`, {
            method: 'PATCH',
            body: JSON.stringify({ status }),
        });
    }
    // Media Domain
    async getUploadUrl(filename, contentType) {
        return this.request('/api/v1/media/upload', {
            method: 'POST',
            body: JSON.stringify({ filename, contentType }),
        });
    }
    async uploadMedia(file) {
        const res = await this.getUploadUrl(file.name, file.type);
        if (!res || !res.uploadUrl || !res.fileUrl) {
            throw new Error('Failed to retrieve upload pre-signed URL');
        }
        const uploadRes = await fetch(res.uploadUrl, {
            method: 'PUT',
            body: file,
            headers: {
                'Content-Type': file.type,
            },
        });
        if (!uploadRes.ok) {
            throw new Error(`Direct-to-storage upload failed with status ${uploadRes.status}`);
        }
        return { url: res.fileUrl };
    }
    // Quotes Domain
    async createQuote(input) {
        return this.request('/api/v1/quotes', {
            method: 'POST',
            body: JSON.stringify(input),
        });
    }
    async getQuotes() {
        return this.request('/api/v1/quotes');
    }
    async getQuote(id) {
        return this.request(`/api/v1/quotes/${id}`);
    }
    async updateQuoteStatus(id, status) {
        return this.request(`/api/v1/quotes/${id}/status`, {
            method: 'PATCH',
            body: JSON.stringify({ status }),
        });
    }
    async convertQuoteToOrder(id) {
        return this.request(`/api/v1/quotes/${id}/convert-to-order`, {
            method: 'POST',
        });
    }
    async generateQuotePdf(id) {
        return this.request(`/api/v1/quotes/${id}/pdf`);
    }
}
